import type { EventDto, EventType, EventVisibility } from '../../api/types';
import { formatDateYYYYMMDD, addDays } from '../../utils/date';

import { isoToDatePart } from './iso';

export type DayGroup<T> = {
  dayKey: string;
  items: T;
};

// ─── Filter types ───────────────────────────────────────────────────────────

export type PeriodFilter = 'today' | '1week' | '2weeks' | '1month' | '2months' | '3months';

export type DashboardFilters = {
  gameTypes: Set<EventType>;
  participation: Set<'participating' | 'invited' | 'none'>;
  creator: Set<'mine' | 'others'>;
  period: PeriodFilter;
  visibility: Set<EventVisibility>;
  availability: Set<'hasSlots' | 'full'>;
};

export function defaultDashboardFilters(): DashboardFilters {
  return {
    gameTypes: new Set<EventType>(['boardgames', 'ping_pong', 'fencing', 'archery', 'hiking', 'courtyard']),
    participation: new Set<'participating' | 'invited' | 'none'>(['participating', 'invited', 'none']),
    creator: new Set<'mine' | 'others'>(['mine', 'others']),
    period: '1week',
    visibility: new Set<EventVisibility>(['open', 'private']),
    availability: new Set<'hasSlots' | 'full'>(['hasSlots', 'full']),
  };
}

/**
 * Compute { from, to } date strings (YYYY-MM-DD) for the given period,
 * starting from today.
 */
export function getPeriodDateRange(period: PeriodFilter): { from: string; to: string } {
  const today = new Date();
  const from = formatDateYYYYMMDD(today);

  const periodDays: Record<PeriodFilter, number> = {
    today: 0,
    '1week': 6,
    '2weeks': 13,
    '1month': 29,
    '2months': 59,
    '3months': 89,
  };

  const to = formatDateYYYYMMDD(addDays(today, periodDays[period]));
  return { from, to };
}

/**
 * Apply all dashboard filters to an array of events.
 * Empty Set means "show all" for that dimension.
 */
export function applyDashboardFilters(params: {
  events: EventDto[];
  filters: DashboardFilters;
  currentUserTelegramId: string;
}): EventDto[] {
  const { events, filters, currentUserTelegramId } = params;

  return events
    .filter((e) => e.status === 'active')
    .filter((e) => filters.gameTypes.has(e.type))
    .filter((e) => filters.visibility.has(e.visibility))
    .filter((e) => {
      const confirmed = e.participants.filter((p) => p.status !== 'pending').length;
      const isFull = confirmed >= e.maxParticipants;
      if (filters.availability.has('hasSlots') && !isFull) return true;
      if (filters.availability.has('full') && isFull) return true;
      return false;
    })
    .filter((e) => {
      const isMine = e.createdByTelegramId === currentUserTelegramId;
      if (filters.creator.has('mine') && isMine) return true;
      if (filters.creator.has('others') && !isMine) return true;
      return false;
    })
    .filter((e) => {
      const participant = e.participants.find((p) => p.telegramId === currentUserTelegramId);
      const isParticipating = participant?.status === 'confirmed';
      const isInvited = participant?.status === 'pending';
      const isNone = !participant;
      if (filters.participation.has('participating') && isParticipating) return true;
      if (filters.participation.has('invited') && isInvited) return true;
      if (filters.participation.has('none') && isNone) return true;
      return false;
    })
    .slice()
    .sort((a, b) => compareIsoLocalDateTime(a.startAt, b.startAt));
}

/**
 * Group a flat list of events by day, then by event type within each day.
 * Returns days sorted ascending, types sorted deterministically.
 */
export function groupAllEventsByDayAndType(
  events: EventDto[],
): Array<DayGroup<Array<[EventType, EventDto[]]>>> {
  const TYPE_ORDER: EventType[] = ['boardgames', 'ping_pong', 'fencing', 'archery', 'hiking', 'courtyard'];

  const dayMap = new Map<string, Map<EventType, EventDto[]>>();

  for (const e of events) {
    const dayKey = isoToDatePart(e.startAt);
    if (!dayKey) continue;
    const day = dayMap.get(dayKey) ?? new Map<EventType, EventDto[]>();
    const list = day.get(e.type) ?? [];
    list.push(e);
    day.set(e.type, list);
    dayMap.set(dayKey, day);
  }

  return Array.from(dayMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([dayKey, typeMap]) => ({
      dayKey,
      items: TYPE_ORDER.filter((t) => typeMap.has(t)).map((t) => [t, typeMap.get(t)!] as [EventType, EventDto[]]),
    }));
}

function compareStrings(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * Compares ISO timestamps in "club" semantics by using date/time string parts.
 * Falls back to Date.parse if not well-formed.
 */
export function compareIsoLocalDateTime(aIso: string, bIso: string): number {
  const aDate = isoToDatePart(aIso);
  const bDate = isoToDatePart(bIso);
  if (aDate && bDate) {
    const dateCmp = compareStrings(aDate, bDate);
    if (dateCmp !== 0) return dateCmp;

    const aTime = aIso.slice(11, 16);
    const bTime = bIso.slice(11, 16);
    if (/^\d{2}:\d{2}$/.test(aTime) && /^\d{2}:\d{2}$/.test(bTime)) return compareStrings(aTime, bTime);
  }

  const aMs = Date.parse(aIso);
  const bMs = Date.parse(bIso);
  if (Number.isFinite(aMs) && Number.isFinite(bMs)) return aMs - bMs;
  return compareStrings(aIso, bIso);
}

export function filterEventsByTypeAndDateRange(params: {
  events: EventDto[];
  type: EventType;
  from: string;
  to: string;
}): EventDto[] {
  const { events, type, from, to } = params;
  return events
    .filter((e) => e.status === 'active')
    .filter((e) => e.type === type)
    .filter((e) => {
      const dayKey = isoToDatePart(e.startAt);
      return dayKey != null && dayKey >= from && dayKey <= to;
    })
    .slice()
    .sort((a, b) => compareIsoLocalDateTime(a.startAt, b.startAt));
}

export function groupEventsByDay(events: EventDto[]): Array<DayGroup<EventDto[]>> {
  const map = new Map<string, EventDto[]>();
  for (const e of events) {
    const dayKey = isoToDatePart(e.startAt);
    if (!dayKey) continue;
    const list = map.get(dayKey) ?? [];
    list.push(e);
    map.set(dayKey, list);
  }

  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([dayKey, list]) => ({ dayKey, items: list }));
}

export function groupBoardgamesByDayAndResource(
  boardgamesInRange: EventDto[],
): Array<DayGroup<Array<[resourceId: string, events: EventDto[]]>>> {
  const map = new Map<string, Map<string, EventDto[]>>();

  for (const e of boardgamesInRange) {
    const dayKey = isoToDatePart(e.startAt);
    if (!dayKey) continue;
    const day = map.get(dayKey) ?? new Map<string, EventDto[]>();
    const resourceId = e.resourceId;
    const list = day.get(resourceId) ?? [];

    list.push(e);
    day.set(resourceId, list);
    map.set(dayKey, day);
  }

  return Array.from(map.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([dayKey, tables]) => ({
      dayKey,
      items: Array.from(tables.entries()).sort((a, b) => a[0].localeCompare(b[0])),
    }));
}
